import { ComplianceAuditStatistics } from '@client/components/compliance-audit/ComplianceAuditStatistics';
import { readIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { createFileRoute, redirect } from '@tanstack/react-router';

// CUR-168 — Audit statistics overlay. Opened from the BarChart3 button on the
// detail page; rendered through the detail route's <Outlet /> so the page stays
// visible behind it. The component fetches its own data and renders the
// OverlayPage — no `orgId` prop here, so it uses the org-scoped endpoint and
// closes back to the audit detail page.

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/details/$complianceAuditId/statistics')({
    // CUR-319 / CUR-778 — the statistics overlay surfaces token cost and, since
    // CUR-778, the audit's AI trace. The detail page hides its entry button unless
    // UI debug info is enabled; this mirrors that so the overlay is unreachable by
    // URL in exactly the cases where the button is hidden.
    //
    // This is a UI-REACHABILITY gate, not authorisation. There is no server-side
    // `enable-debug-info` check: the trace procedures are gated by the compliance
    // audit module, the `enable-compliance-audit` flag and
    // `compliance_audit:access`, and a caller holding those can call them with this
    // toggle off. Nothing behind this redirect may be treated as privileged on the
    // strength of the redirect alone.
    //
    // `beforeLoad` cannot call hooks, so it reads the SAME two inputs — the
    // debug-panel toggle and the `enable-debug-info` flag — through
    // `readIsUIDebugInfoEnabled()`, the imperative twin of the button's
    // `useIsUIDebugInfoEnabled()`. Both evaluate one shared predicate, so the
    // hidden button and the redirected URL cannot disagree.
    //
    // Awaited, not sync: on a hard load (refresh / new tab / pasted URL) this guard
    // runs before PostHog has finished initialising, when every flag reader still
    // answers with its default. `readIsUIDebugInfoEnabled()` owns that wait, so an
    // enabled user is not bounced off their own URL by a race.
    beforeLoad: async ({ params }): Promise<void> => {
        if (!(await readIsUIDebugInfoEnabled())) {
            throw redirect({
                to: '/console/$orgId/tools/compliance-audit/details/$complianceAuditId',
                params,
                replace: true,
            });
        }
    },
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Audit statistics',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return <ComplianceAuditStatistics complianceAuditId={Route.useParams().complianceAuditId} />;
}
